'use client'

import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { lazyComponent } from 'utils/lazy-component'
import { GrowthBookProvider } from 'growthbook'
import { Z_INDEX_MODALS } from 'constants/modals'
import type { UseCase, DefaultPlan } from 'cf-types'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { PaymentsAppTypeContext } from './payments-app-type'
import type { PaymentsAppType } from './payments-app-type'
import { TeamDashboardHrefContext } from './team-dashboard-href'
import type { PaymentsData } from '../payments-dialog/context/payments-dialog-types'

// oxlint-disable-next-line cf-web/no-barrel-file
export { type PaymentsAppType, usePaymentsAppType, getModalLocation } from './payments-app-type'

const PaymentsDialog = lazyComponent(() =>
  import('../payments-dialog').then(mod => mod.PaymentsDialog),
)

export const PaymentsDialogContext = React.createContext<{
  open: (plan?: DefaultPlan, useCase?: UseCase) => void
}>({
  open: () => {
    throw new Error('PaymentsDialogContext not initialized')
  },
})

const zIndex = Z_INDEX_MODALS.payment.react

export const PaymentsDialogProvider = ({
  children,
  useCase = 'purchase',
  appType = 'marketplace',
  currency,
  teamDashboardHref = null,
}: {
  children: React.ReactNode
  useCase?: UseCase
  appType?: PaymentsAppType
  currency?: PaymentsData['currency']
  teamDashboardHref?: string | null
}) => {
  const [open, setOpen] = React.useState(false)
  const [defaultPlan, setDefaultPlan] = React.useState<DefaultPlan>('freeTrial')
  const [internalUseCase, setInternalUseCase] = React.useState<UseCase>(useCase)

  return (
    <PaymentsAppTypeContext.Provider value={appType}>
      <TeamDashboardHrefContext.Provider value={teamDashboardHref}>
        <GrowthBookProvider>
          <PaymentsDialogContext.Provider
            value={{
              open: (plan: DefaultPlan = 'freeTrial', useCase: UseCase = 'purchase') => {
                if (document.querySelector('[data-payments-dialog]')) {
                  return
                }
                setDefaultPlan(plan)
                setInternalUseCase(useCase)
                setOpen(true)
              },
            }}
          >
            <Dialog.Root open={open} onOpenChange={setOpen} defaultOpen={false}>
              <Dialog.Portal>
                <Dialog.Body
                  dismissable={false}
                  overlayZIndex={zIndex - 1}
                  zIndex={zIndex}
                  data-payments-dialog
                  data-lumo-interactive
                >
                  <React.Suspense fallback={<AnimatedSpinnerIcon />}>
                    <PaymentsDialog.component
                      currency={currency}
                      defaultPlan={defaultPlan}
                      useCase={internalUseCase}
                    />
                  </React.Suspense>
                </Dialog.Body>
              </Dialog.Portal>
            </Dialog.Root>
            {children}
          </PaymentsDialogContext.Provider>
        </GrowthBookProvider>
      </TeamDashboardHrefContext.Provider>
    </PaymentsAppTypeContext.Provider>
  )
}

export const usePaymentsDialog = () => {
  const context = React.useContext(PaymentsDialogContext)

  if (context === undefined) {
    throw new Error('usePaymentsDialog must be used within a PaymentsDialogContext')
  }

  return context
}
